import { useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { apiAxios } from "@/api/config";
import { useNotification } from "@/providers/NotificationProvider";
import { mutedText } from "@/styles/variants";
import { ui, notifications } from "@/strings";
import PiEnvelopeDefaultStroke from "@markeroo/icons/src/stroke/communication/PiEnvelopeDefaultStroke";

const logoUrl = new URL("../../images/logo-full.png", import.meta.url).href;

export function CheckEmailPage() {
  const location = useLocation();
  const { show } = useNotification();
  const email = (location.state as { email?: string })?.email || "";

  const [isResending, setIsResending] = useState(false);
  const [resent, setResent] = useState(false);

  const handleResend = () => {
    if (!email || isResending) return;

    setIsResending(true);
    apiAxios
      .post("/auth/resend-verification", { email })
      .then(() => {
        show(notifications.auth.verificationResent, "success");
        setResent(true);
      })
      .catch(() => {
        show(notifications.auth.verificationResendFailed, "error");
      })
      .finally(() => {
        setIsResending(false);
      });
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface-page px-4 py-10">
      <div className="flex w-full max-w-md flex-col items-center gap-6 rounded-2xl bg-white p-8 shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
        <img src={logoUrl} alt="Markeroo logo" className="h-20 w-auto" />

        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-100">
          <PiEnvelopeDefaultStroke className="h-6 w-6 text-brand-600" aria-hidden="true" />
        </div>

        <h1 className="text-xl font-semibold text-theme-primary">{ui.labels.checkYourEmail}</h1>

        <p className={`${mutedText} text-center`}>
          {ui.labels.verificationSent}{" "}
          {email && <strong className="text-theme-secondary">{email}</strong>}
        </p>

        <p className="text-center text-sm text-theme-muted">
          {ui.labels.emailVerificationRequired}
        </p>

        {email && (
          <button
            type="button"
            onClick={handleResend}
            disabled={isResending || resent}
            className="inline-flex items-center justify-center cursor-pointer text-label-md font-semibold text-brand-600 transition hover:text-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isResending
              ? ui.actions.resendingVerification
              : resent
                ? notifications.auth.verificationResent
                : ui.actions.resendVerificationEmail}
          </button>
        )}

        <div className="mt-2 flex flex-col items-center gap-2">
          <Link to="/login" className="inline-flex items-center justify-center cursor-pointer text-label-md font-semibold text-brand-600 transition hover:text-brand-700">
            {ui.actions.backToLogin}
          </Link>
        </div>
      </div>
    </div>
  );
}
